import { useMemo } from "react"

import { api } from "~/utils/api"

export type AcceptedMatch = {
  match: string
  similarity: number | null
  // Off `Sku`, not the SkuStore: an accepted comparable can be a variant the store never loads.
  productName: string | null
  variantName: string | null
  imageUrl: string | null
  productId?: string | null
}
export type AcceptedMatchInfo = {
  matchCount: number
  avgSimilarity: number | null
  matches: AcceptedMatch[]
  // Reviewed as having no comparable, so a zero baseline reads apart from an unreviewed one.
  noComparable: boolean
  // The waiver's level, which the engine plans at. Null = no number stored; plans off own sales.
  noComparableBaseline: number | null
  // The share of that level expected as pre-orders; null = the engine uses the average's.
  noComparablePreorderShare: number | null
}

// Fashion reads baseline matches, NOOS forecast ones. No entry = the NPI baseline is a fallback.
export const useAcceptedMatches = (kind: "fashion" | "noos", collectionIds: number[]) => {
  const enabled = collectionIds.length > 0
  const fashionQ = api.demand2.getCustomMatchesForCollection.useQuery(
    { collectionIds },
    { enabled: enabled && kind === "fashion" },
  )
  const noosQ = api.demand2.getNoosCustomMatchCounts.useQuery(
    { collectionIds },
    { enabled: enabled && kind === "noos" },
  )
  const rows = kind === "fashion" ? fashionQ.data : noosQ.data
  return useMemo(() => {
    const m = new Map<string, AcceptedMatchInfo>()
    for (const r of rows ?? []) {
      m.set(r.variantId, {
        matchCount: r.matchCount,
        avgSimilarity: r.avgSimilarity,
        matches: r.matches,
        noComparable: r.noComparable,
        noComparableBaseline: r.noComparableBaseline,
        noComparablePreorderShare: r.noComparablePreorderShare,
      })
    }
    return m
  }, [rows])
}

/**
 * How much of an NPI worklist is unanswered; an accepted comparable and a no-comparable waiver both
 * count as answers. Shared by the pill badge and the worklist inspector. A variant planning off
 * its own sales is nothing to answer here.
 */
export const npiWorklistProgress = (
  allNpi: {
    variantId: string
    startDate?: Date | null
    ownSales?: boolean
  }[],
  matchMap: Map<string, AcceptedMatchInfo>,
  kind: "fashion" | "noos",
) => {
  // A match on it is never used, so it is no work left to do.
  const npi = allNpi.filter((v) => !v.ownSales)
  let matched = 0
  let waived = 0
  let needsActivation = 0
  let outstandingNoos = 0
  for (const v of npi) {
    const info = matchMap.get(v.variantId)
    const hasMatch = (info?.matchCount ?? 0) > 0
    const noComparable = info?.noComparable === true
    if (hasMatch) matched++
    else if (noComparable) waived++
    // NOOS also needs a start date; fashion uses the collection's window.
    if (kind === "noos") {
      const activated = v.startDate != null
      if (!activated) needsActivation++
      // Counted once per variant.
      if (!activated || (!hasMatch && !noComparable)) outstandingNoos++
    }
  }
  const unresolved = npi.length - matched - waived
  return {
    total: npi.length,
    matched,
    waived,
    unresolved,
    needsActivation,
    outstanding: kind === "noos" ? outstandingNoos : unresolved,
  }
}

export const NPI_STATUS_ORDER = [
  "Unmatched",
  "Activate",
  "No comparable",
  "Matched",
  "Own sales",
] as const

export type NpiStatus = (typeof NPI_STATUS_ORDER)[number]

/** The statuses a kind can produce: only NOOS activates or plans off own sales. */
export const npiStatusesFor = (kind: "fashion" | "noos"): NpiStatus[] =>
  kind === "noos" ? [...NPI_STATUS_ORDER] : ["Unmatched", "No comparable", "Matched"]

/** A new variant's place on the matching worklist, as its Status column reads. */
export const npiMatchStatus = (
  npi: { startDate?: Date | null; ownSales?: boolean } | undefined,
  info: AcceptedMatchInfo | undefined,
  kind: "fashion" | "noos",
): NpiStatus => {
  if (npi?.ownSales) return "Own sales"
  if (info?.noComparable === true) return "No comparable"
  if ((info?.matchCount ?? 0) === 0) return "Unmatched"
  // No start date at all (activation date nor first sale): it can't plan until one is set.
  if (kind === "noos" && npi?.startDate == null) return "Activate"
  return "Matched"
}
